import styled from "@emotion/styled";
import { Button, Flex, Input } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useModal } from "context/ModalStackManager";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { convertHangulToEng } from "utils/koToEngMap";
import { customNotification } from "utils/notificationShow";

export const BarcodeInputForm = () => {
  const { closeModal } = useModal();
  const { t } = useTranslation();
  const [inputs, setInputs] = useState<string[]>([""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [barcodeQueue, setBarcodeQueue] = useState<string[]>([]);

  const handleInputChange = (rawInput: string, index: number) => {
    const cleanedInput = convertHangulToEng(rawInput)
      .replace(/\r?\n/g, "")
      .trim();

    let lotId: string | undefined;

    // 숫자만 입력한 경우도 허용
    if (/^\d+$/.test(cleanedInput)) {
      lotId = cleanedInput;
    } else {
      const lotIdMatch = cleanedInput.match(
        /^(lotId|ㅣㅐ샹|ㅣㅒ썅|LOTID):(\d+)$/i
      );
      lotId = lotIdMatch?.[2];
    }

    // itemCode나 quantity가 포함된 경우 무시하고 초기화
    if (
      cleanedInput.includes("itemCode") ||
      cleanedInput.includes("quantity") ||
      cleanedInput.includes("it드챙e") ||
      cleanedInput.includes("벼무샤쇼") ||
      cleanedInput.includes("lo수믇") ||
      cleanedInput.includes("lotName") ||
      cleanedInput.includes("itemName") ||
      cleanedInput.includes("it드N믇")
    ) {
      const updatedInputs = [...inputs];
      updatedInputs[index] = "";
      setInputs(updatedInputs);
      return;
    }

    if (!lotId) return;

    if (inputs.some((input, i) => input === lotId && i !== index)) {
      customNotification.error({
        message: lotId + t("번은 이미 추가된 LOT 입니다."),
      });
      const updatedInputs = [...inputs];
      updatedInputs[index] = "";
      setInputs(updatedInputs);
      return;
    }

    const filtered = inputs.filter(
      (v, i) => v !== "" && v !== undefined && i !== index
    );
    setInputs([...filtered, lotId, ""]);

    requestAnimationFrame(() => {
      inputRefs.current[filtered.length + 1]?.focus();
    });
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleInputChange(e.currentTarget.value, index);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newInputs = [...inputs];
    newInputs[index] = event.target.value;
    setInputs(newInputs);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>, index: number) => {
    handleInputChange(e.currentTarget.value, index);
  };

  const handleRemoveInput = (index: number) => {
    setInputs((prevInputs) => prevInputs.filter((_, i) => i !== index));
    inputRefs.current = inputRefs.current.filter((_, i) => i !== index);
  };

  useEffect(() => {
    setInputs([""]);
    inputRefs.current = [];
  }, []);

  useEffect(() => {
    if (inputRefs.current.length > 0) {
      inputRefs.current[inputRefs.current.length - 1]?.focus();
    }
  }, [inputs]);

  useEffect(() => {
    if (barcodeQueue.length === 0) return;

    setInputs((prevInputs) => {
      const updated = [...prevInputs];
      // 줄바꿈 모두 제거 후 한 줄로 만듦
      let barcode = barcodeQueue[0].replace(/\r?\n/g, "");

      // itemCode가 나오면 그 앞까지만 남김
      const itemCodeIdx = barcode.indexOf("itemCode");
      if (itemCodeIdx !== -1) {
        barcode = barcode.slice(0, itemCodeIdx);
      }

      // lotId만 추출
      const match = barcode.match(/lotId:(\d+)/);
      const onlyLotId = match?.[1] ?? "";

      updated[updated.length - 1] = onlyLotId;
      return [...updated, ""];
    });

    requestAnimationFrame(() => {
      inputRefs.current[inputRefs.current.length - 1]?.focus();
    });

    setBarcodeQueue((prev) => prev.slice(1));
  }, [barcodeQueue]);

  const handleReturnBarcodeInputs = () => {
    const values = inputs.filter((input) => input !== "");
    closeModal(values);
  };

  return (
    <Flex direction="column" gap="sm">
      {inputs.map((input, index) => (
        <InputWrapper key={index}>
          <InputLabel>{index + 1}</InputLabel>
          <Input
            value={input}
            placeholder={t("Enter Input And Take a Barcode")}
            aria-label="Enter Input And Take a Barcode"
            data-autofocus
            // handleBlur={(e) =>handleBlur(eval)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onChange={(event) => handleChange(event, index)}
            style={{ marginBottom: "10px" }}
            ref={(el) => (inputRefs.current[index] = el)}
            disabled={index !== inputs.length - 1}
          />
          {index !== 0 && (
            <Button
              leftIcon={<IconTrash />}
              color="red"
              variant="white"
              onClick={() => handleRemoveInput(index)}
            >
              {t("삭제")}
            </Button>
          )}
        </InputWrapper>
      ))}
      <ButtonContainer>
        <Button onClick={handleReturnBarcodeInputs}>{t("투입")}</Button>
        <Button onClick={() => closeModal([])}>{t("닫기")}</Button>
      </ButtonContainer>
    </Flex>
  );
};

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  margin-left: 15px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 50px;
  margin-top: 20px;
`;

const InputLabel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #007bff;
  color: white;
  font-size: 1rem;
  font-weight: bold;
  margin-right: 10px;
`;
